import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery, pyrotchBaseUrl } from "../axios/AxiosInstance";
import { CardResponse } from "../../models/Card";
import { LeaderboardData } from "../../models/LeaderBoard";

export const CardApi = createApi({
  baseQuery: axiosBaseQuery({
    baseUrl: pyrotchBaseUrl + "v1/",
  }),
  reducerPath: "CardApi",
  tagTypes: ["Card"],

  endpoints(build) {
    return {
      createCard: build.mutation<
        any,
        {
          createCard: any;
          files: File[];
        }
      >({
        query: ({ createCard, files }) => {
          const formData = new FormData();

          formData.append("createCard", JSON.stringify(createCard));

          files.forEach((file) => {
            formData.append("files", file);
          });

          return {
            url: `cards/create`,
            method: "POST",
            data: formData,
            params: createCard,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          };
        },
        invalidatesTags: ["Card"],
      }),

      getCards: build.query<CardResponse, { page: number; size: number; startDate: string; endDate: string }>({
        query: ({ page, size, startDate, endDate }) => ({
          url: `cards?page=${page}&size=${size}&startDate=${startDate}&endDate=${endDate}`,
          method: "GET",
        }),
        providesTags: ["Card"],
      }),

      requestApprovalCard: build.mutation<
        any,
        { cardId: number; remarks: string; files: File[] }
      >({
        query: ({ cardId, remarks, files }) => {
          const formData = new FormData();

          // Append remarks as a string field
          formData.append("remarks", remarks);

          // Append files as binary data
          files.forEach((file) => {
            formData.append("files", file); // 'files' field to match the expected API parameter
          });

          return {
            url: `cards/sendForApproval/${cardId}`, // Replace with the actual API endpoint
            method: "POST",
            data: formData,
            headers: {
              "Content-Type": "multipart/form-data", // Required for FormData
            },
          };
        },
        invalidatesTags: ["Card"], // Invalidate tags as per requirement
      }),

      deleteCard: build.mutation<void, number>({
        query: (cardId) => {
          return {
            url: `cards/delete/${cardId}`,
            method: "DELETE",
          };
        },
        invalidatesTags: ["Card"],
      }),

      approveDeclineCard: build.mutation<
        any,
        { cardId: number; approved: boolean; remarks: string }
      >({
        query: ({ cardId, approved, remarks }) => ({
          url: `cards/approveOrDecline/${cardId}`,
          method: "POST",
          data: {
            approved,
            remarks,
          },
        }),
        invalidatesTags: ["Card"],
      }),

      editCard: build.mutation<
        any,
        {
          cardId: any;
          cardOption: string;
          projectTask: string;
          description: string;
          assignedBy: number;
          projectId: number;
          myTaskId: number,
          othersTask: string,
        }
      >({
        query: ({
          cardId,
          cardOption,
          projectTask,
          description,
          assignedBy,
          projectId,
          myTaskId,
          othersTask
        }) => ({
          url: `cards/edit/${cardId}`,
          method: "PUT",
          data: { cardOption, projectTask, description, assignedBy, projectId, myTaskId, othersTask },
        }),
        invalidatesTags: ["Card"],
      }),

      getLeaderboardData: build.query<
        LeaderboardData,
        { startDate: string; endDate: string }
      >({
        query: ({ startDate, endDate }) => ({
          url: `cards/leaderboard?startDate=${startDate}&endDate=${endDate}`,
          method: "GET",
        }),
      }),
    };
  },
});

export const {
  useCreateCardMutation,
  useGetCardsQuery,
  useRequestApprovalCardMutation,
  useDeleteCardMutation,
  useApproveDeclineCardMutation,
  useEditCardMutation,
  useGetLeaderboardDataQuery,
  useLazyGetLeaderboardDataQuery
} = CardApi;
